import { PageHeader } from "@/widgets/page-header"
import DatePicker from "react-datepicker"
import { Controller, FieldError, SubmitHandler, useForm } from "react-hook-form"
import CalendarIcon from "@/shared/assets/images/CalendarBlank.svg"
import { FormInput } from "@/shared/ui/form-input"
import XSvg from '@/shared/assets/images/X.svg'
import { yupResolver } from "@hookform/resolvers/yup"
import WarningSvg from '@/shared/assets/images/Warning.svg'
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { selectAvailableBonuses } from "@/entities/bonus"
import { Fragment, useEffect, useMemo, useState } from "react"
import * as yup from 'yup'
import ReactSelect from "react-select"
import ArrowDown from '@/shared/assets/images/ArrowDown.svg'
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { createCampaign, MarketingEvents } from "@/shared/api/marketing-campaigns"
import { fetchInvitations, selectInvitations, selectInvitationsFirstLoad } from "@/entities/invitation"
import { fetchCampaigns, fetchChannels, selectChannels, selectChannelsFirstLoad } from "@/entities/marketing-campaign"
import { useNavigate } from "react-router-dom"
import { truncateString } from "@/shared/lib/truncate"

const marketingEventNamesSelectOptions: {value: MarketingEvents, label: string}[] = [
    {
        value: "UserRegisteredDomainEvent",
        label: "Регистрация нового пользователя",
    },
    {
        value: "InvitationActivatedDomainEvent",
        label: "Переход по ссылке",
    }
]

type MarketingCampaignType = "draw" | "default"

const marketingCampaignTypesSelectOptions: { value: MarketingCampaignType, label: string }[] = [
    {
        value: "default",
        label: "Стандартная кампания",
    },
    {
        value: "draw",
        label: "Розыгрыш",
    }
]

interface CreateCampaignFormValues {
    start_date: Date | undefined,
    end_date: Date | undefined,
    name: string,
    invitation_id?: string,
    limit: number
    events: string[]
    bonuses: {
        place: string,
        id: string,
        amount: number
    }[],
    channels: number[],
    type: MarketingCampaignType
    auto_post_channel?: number
    auto_post_results: boolean
}

const initial: CreateCampaignFormValues = {
    start_date: undefined,
    end_date: undefined,
    name: "",
    limit: 1,
    events: [],
    bonuses: [],
    channels: [],
    type: null,
    auto_post_results: false,
}

const createCampaignValidationSchema = yup.object().shape({
    name: yup.string().required("Введите название").max(255, "Максимальная длина - 255"),
    invitation_id: yup.string().when("events", (events, schema) => {
        if (events.includes(MarketingEvents.InvitationActivated)) {
            return schema.required("Заполните это поле")
        }
        return schema
    }),
    start_date: yup.date().required("Укажите дату начала"),
    end_date: yup.date().min(yup.ref('start_date'), "Некорректная дата окончания").required("Укажите дату окончания"),
    limit: yup.number().min(1, 'Минимальный лимит - 1').typeError("Укажите число").required("Укажите лимит"),
    events: yup.array().of(yup.string()).when("type", (type, schema) => {
        if (type.includes("draw")) {
            return schema.min(0, "Выберите условия выполнения")
        }
        return schema.min(1, "Выберите условия выполнения")
    }).required("Выберите условия выполнения"),
    channels: yup.array().of(yup.number()).when("type", (type, schema) => {
        if (type.includes("draw")) {
            return schema.min(1, "Выберите каналы").required("Выберите каналы")
        }
        return schema.min(0, "Выберите каналы").required("Выберите каналы")
    }),
    bonuses: yup.array().min(1, "Выберите бонусы").of(yup.object().shape({
        place: yup.string().required("Заполните это поле"),
        id: yup.string().required("Выберите бонус"),
        amount: yup.number().typeError("Укажите число").required("Укажите количество")
    })).required("Выберите бонусы"),
    type: yup.string().oneOf(["draw", "default"], "Выберите тип акции").required("Выберите тип акции"),
    auto_post_results: yup.boolean(),
    auto_post_channel: yup.number().when("auto_post_results", (type, schema) => {
        if (type.includes(true)) {
            return schema.required("Укажите канал")
        }
        return schema
    }),
})

export const CreateMarketingCampaignPage = () => {
    const bonuses = useAppSelector(selectAvailableBonuses)
    const invitationsFirstLoad = useAppSelector(selectInvitationsFirstLoad)
    const channelsFirstLoad = useAppSelector(selectChannelsFirstLoad)
    const channels = useAppSelector(selectChannels)
    const invitations = useAppSelector(selectInvitations)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    useEffect(() => {
        if (invitationsFirstLoad) {
            dispatch(fetchInvitations())
        }
        if (channelsFirstLoad) {
            dispatch(fetchChannels())
        }
    }, [])

    const invitationsSelectOptions = useMemo(() => invitations.map((invitation) => ({
        value: invitation.id,
        label: invitation.group.name
    })), [invitations])

    const channelsSelectOptions = useMemo(() => channels.map((channel) => ({
        value: channel.telegramId,
        label: channel.name
    })), [channels])

    const [selectedBonuses, setSelectedBonuses] = useState<{
        place: string,
        id: string,
        amount: number
    }[]>([{ place: '1', id: '', amount: null }])

    const bonusSelectOptions = useMemo(() => bonuses.map((bonus) => ({
        value: bonus.id,
        label: bonus.name
    })), [bonuses])

    const addBonus = () => {
        setSelectedBonuses(prev => [...prev, { place: '', id: '', amount: null }])
    }

    const removeBonus = (idx: number) => {
        if (idx === 0) return
        setSelectedBonuses(prev => prev.filter((_, i) => i !== idx))
    }

    const {
        register,
        control,
        reset,
        getValues,
        watch,
        handleSubmit,
        setValue,
        formState: {
            errors,
        },
        clearErrors,
        setError
    } = useForm({
        defaultValues: initial,
        resolver: yupResolver(createCampaignValidationSchema)
    })

    const validateBonuses = () => {
        const limit = getValues('limit')
        const coveredPlaces = new Set<number>()
        const errors = []
        for (let idx = 0; idx < selectedBonuses.length; idx++) {
            const bonus = selectedBonuses[idx]
            if (bonus.place.includes('-')) {
                const [startStr, endStr] = bonus.place.split('-')
                const start = Number(startStr)
                const end = Number(endStr)

                if (isNaN(start) || isNaN(end) || start > end || start < 1 || end > limit) {
                    errors.push("Некорректный интервал мест")
                    setError(`bonuses.${idx}.place`, {
                       message: "Некорректный интервал мест",
                       type: "custom"
                    })
                    continue
                } else {
                    clearErrors(`bonuses.${idx}.place`)
                }

                for (let i = start; i <= end; i++) {
                    if (coveredPlaces.has(i)) {
                        errors.push("Некорректный интервал мест")
                        setError(`bonuses.${idx}.place`, {
                            message: `Место ${i} указано более одного раза`,
                            type: "custom"
                        })
                        continue
                    } else {
                        clearErrors(`bonuses.${idx}.place`)
                    }
                    coveredPlaces.add(i)
                }
            } else {
                const place = Number(bonus.place)
                if (isNaN(place) || place < 1 || place > limit) {
                    errors.push("Некорректное место")
                    setError(`bonuses.${idx}.place`, {
                       message: "Некорректное место",
                       type: "custom"
                    })
                    continue
                } else {
                    clearErrors(`bonuses.${idx}.place`)
                }

                if (coveredPlaces.has(place)) {
                    errors.push("Место ${place} указано более одного раза")
                    setError(`bonuses.${idx}.place`, {
                        message: `Место ${place} указано более одного раза`,
                        type: "custom"
                    })
                } else {
                    clearErrors(`bonuses.${idx}.place`)
                }
                coveredPlaces.add(place)
            }
        }
        
        const allPlaces = Array.from({ length: limit }, (_, i) => i + 1);
        const missingPlaces = allPlaces.filter(p => !coveredPlaces.has(p));
        if (missingPlaces.length > 0) {
            errors.push(`Не указаны призы для мест: ${missingPlaces.join(', ')}`)
            const error = truncateString(`Не указаны призы для мест: ${missingPlaces.join(', ')}`, 50)
            setError(`bonuses`, {
                message: error,
                type: "custom"
            })
        }

        if (errors.length === 0) {
            clearErrors('bonuses')
        }
    }

    const onSubmit: SubmitHandler<CreateCampaignFormValues> = async (data: CreateCampaignFormValues) => {
        const prizes = data.bonuses.reduce((acc, curr) => {
            if (curr.place.includes('-')) {
                const [startStr, endStr] = curr.place.split('-')
                const start = Number(startStr)
                const end = Number(endStr)
                const prizes = []
                for (let i = start; i <= end; i++) {
                    prizes.push({...curr, place: i})
                }
                return [...acc, ...prizes]
            } else {
                return [...acc, {...curr, place: Number(curr.place)}]
            }
        }, [])
        try {
            await createCampaign({
                name: data.name,
                start_date: data.start_date.toISOString(),
                end_date: data.end_date.toISOString(),
                prizes,
                limit: data.limit,
                invitation_id: data.invitation_id,
                events: data.events as MarketingEvents[],
                is_draw: data.type === "draw",
                channels: data.channels,
                auto_post_results: data.auto_post_results,
                auto_post_channel: data.auto_post_channel
            })
            notify.success("Маркетинговая кампания успешно создана")
            navigate('/marketing-campaigns')
            dispatch(fetchCampaigns())
            resetAll()
        } catch (e) {
            notify.error(getErrorMessage(e.response?.data?.error_id))
        }
    }

    const resetAll = () => {
        reset()
        setSelectedBonuses([])
    }

    const showSelectInvitation = useMemo(() => getValues('events').find(event => event === MarketingEvents.InvitationActivated), [watch('events')])

    useEffect(() => {
        setValue('auto_post_channel', undefined)
        setValue('auto_post_results', false)
    }, [watch('type')])

    useEffect(() => {
        setValue('bonuses', selectedBonuses)
    }, [errors, selectedBonuses])

    useEffect(() => {
        validateBonuses()
    }, [selectedBonuses, watch])

    return (
        <div className="_page_container">
            <PageHeader title="Маркетинговые кампании" />
            <div className="flexbox">
                <div className="box">
                    <div className="box_header font-mediumttf">
                        Создание кампании
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-6">
                                <FormInput
                                    id="name"
                                    type="text"
                                    required
                                    label="Название"
                                    register={register}
                                    error={errors['name']}
                                    placeholder="Введите название"
                                />
                            </div>
                            <div className="col-span-6">
                                <FormInput
                                    id="limit"
                                    type="number"
                                    required
                                    error={errors['limit']}
                                    label="Количество призовых мест"
                                    register={register}
                                    placeholder="Введите количество призовых мест"
                                />
                            </div>
                            <div className="col-span-6">
                                <div className={`flex justify-between flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors['start_date'] && 'border-color-danger'}`}>
                                    <div className="flex mb-1 justify-between w-full items-center">
                                        <label className="font-semibold uppercase text-base text-[#828282]">
                                            Дата начала акции
                                        </label>
                                        {errors['start_date'] &&
                                            <label className="text-base flex items-center text-color-danger">
                                                <WarningSvg width={20} height={20} className="me-1" />
                                                {'Заполните это поле'}
                                            </label>
                                        }
                                    </div>
                                    <Controller
                                        control={control}
                                        name={'start_date'}
                                        render={({ field }) => (
                                            <DatePicker
                                                placeholderText={'Выберите дату начала акции'}
                                                required={true}
                                                wrapperClassName="react-datepicker-form"
                                                dateFormat={"dd.MM.yyyy HH:mm"}
                                                popperClassName="react-datepicker-form-popper"
                                                showIcon
                                                onChange={(date: Date | null) => setValue('start_date', date)}
                                                enableTabLoop={false}
                                                selected={field.value}
                                                popperProps={{
                                                    placement: 'bottom',
                                                }}
                                                showTimeSelect
                                                portalId="root"
                                                icon={(<CalendarIcon width={24} height={24} />)}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="col-span-6">
                                <div className={`flex justify-between flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors['end_date'] && 'border-color-danger'}`}>
                                    <div className="flex mb-1 justify-between w-full items-center">
                                        <label className="font-semibold uppercase text-base text-[#828282]">
                                            Дата окончания
                                        </label>
                                        {errors['end_date'] &&
                                            <label className="text-base flex items-center text-color-danger">
                                                <WarningSvg width={20} height={20} className="me-1" />
                                                {'Заполните это поле'}
                                            </label>
                                        }
                                    </div>
                                    <Controller
                                        control={control}
                                        name={'end_date'}
                                        render={({ field }) => (
                                            <DatePicker
                                                placeholderText={'Выберите дату окончания акции'}
                                                required={true}
                                                wrapperClassName="react-datepicker-form"
                                                dateFormat={"dd.MM.yyyy HH:mm"}
                                                popperClassName="react-datepicker-form-popper"
                                                showIcon
                                                onChange={(date: Date | null) => setValue('end_date', date)}
                                                enableTabLoop={false}
                                                selected={field.value}
                                                popperProps={{
                                                    placement: 'bottom',
                                                }}
                                                showTimeSelect
                                                portalId="root"
                                                icon={(<CalendarIcon width={24} height={24} />)}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="col-span-12">
                                <FormInput
                                    id="type"
                                    type="select"
                                    required
                                    label="Тип акции"
                                    control={control}
                                    error={errors['type'] as FieldError}
                                    options={marketingCampaignTypesSelectOptions}
                                    placeholder="Выберите тип акции"
                                />
                            </div>
                            {watch('type') === 'draw'
                                ? (<>
                                    <div className="col-span-12">
                                        <FormInput
                                            id="channels"
                                            type="select"
                                            required
                                            label="Каналы"
                                            isMulti
                                            control={control}
                                            error={errors['channels'] as FieldError}
                                            options={channelsSelectOptions}
                                            placeholder="Выберите каналы"
                                        />
                                    </div>
                                    <div className="col-span-12">
                                        <FormInput
                                            id="events"
                                            type="select"
                                            required
                                            label="Условия выполнения (опционально)"
                                            isMulti
                                            control={control}
                                            error={errors['events'] as FieldError}
                                            options={marketingEventNamesSelectOptions}
                                            placeholder="Выберите условия выполнения"
                                        />
                                    </div>
                                    <div className="col-span-6">
                                        <FormInput
                                            id="auto_post_results"
                                            type="toggle"
                                            required
                                            register={register}
                                            label="Автоматическая рассылка результатов"
                                            error={errors['events'] as FieldError}
                                            placeholder="Автоматическая рассылка результатов"
                                        />
                                    </div>
                                    {watch('auto_post_results') &&
                                        <div className="col-span-6">
                                            <FormInput
                                                id="auto_post_channel"
                                                type="select"
                                                required
                                                label="Канал"
                                                control={control}
                                                error={errors['auto_post_channel'] as FieldError}
                                                options={channelsSelectOptions}
                                                placeholder="Выберите канал"
                                            />
                                        </div>      
                                    }
                                </>)
                                : (<>                                
                                    <div className="col-span-12">
                                        <FormInput
                                            id="events"
                                            type="select"
                                            required
                                            label="Условия выполнения"
                                            isMulti
                                            control={control}
                                            error={errors['events'] as FieldError}
                                            options={marketingEventNamesSelectOptions}
                                            placeholder="Выберите условия выполнения"
                                        />
                                    </div>
                                </>)
                            }
                            {showSelectInvitation &&
                                <div className="col-span-12">
                                    <FormInput
                                        id="invitation_id"
                                        type="select"
                                        required
                                        label="Приглашение"
                                        control={control}
                                        error={errors['invitation_id'] as FieldError}
                                        options={invitationsSelectOptions}
                                        placeholder="Выберите приглашение"
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </div>
                <div className="box">
                    <div className="box_header font-mediumttf flex items-center justify-between">
                        <div>
                            Призовые места
                        </div>
                        {errors['bonuses'] &&
                            <label className="text-sm font-manrope font-normal flex items-center text-color-danger">
                                <WarningSvg width={20} height={20} className="me-1" />
                                {errors['bonuses'].message}
                            </label>
                        }
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                        {selectedBonuses.map((b, idx) =>
                            <Fragment key={idx}>
                                <div className="col-span-4">
                                    <div className={`flex w-full flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors['bonuses']?.[idx]?.place && 'border-color-danger'}`}>
                                        <div className="flex justify-between w-full items-center">
                                            <label className="font-semibold uppercase text-base text-[#828282]">
                                                Место
                                            </label>
                                            {errors['bonuses']?.[idx]?.place &&
                                                <label className="text-base flex items-center text-color-danger">
                                                    <WarningSvg width={20} height={20} className="me-1" />
                                                    {errors['bonuses']?.[idx]?.place.message}
                                                </label>
                                            }
                                        </div>
                                        <input
                                            placeholder={'Например 1 или 1-3'}
                                            className={`w-full outline-0 mt-[4px] text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base outline-none text-color-dark`}
                                            value={b.place}
                                            onChange={(e) => setSelectedBonuses(prev => prev.map((item, index) => index === idx ? ({ ...item, place: e.target.value }) : item))}
                                        />
                                    </div>
                                </div>
                                <div className="col-span-4">
                                    <div className={`flex relative flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors['bonuses']?.[idx]?.id && 'border-color-danger'}`}>
                                        <div className="flex justify-between w-full items-center">
                                            <label className="font-semibold uppercase text-base text-[#828282]">
                                                Бонус
                                            </label>
                                            {errors['bonuses']?.[idx]?.id &&
                                                <label className="text-base flex items-center text-color-danger">
                                                    <WarningSvg width={20} height={20} className="me-1" />
                                                    {errors['bonuses']?.[idx].id.message}
                                                </label>
                                            }
                                        </div>
                                        <ReactSelect
                                            classNames={{
                                                control: () => 'w-full !outline-0 !shadow-none !min-h-[auto] !border-0 !border-none !p-0 !outline-none ',
                                                valueContainer: () => '!border-0 !outline-0 !p-0 text-color-dark text-lg',
                                                placeholder: () => 'text-color-dark/70 text-base !m-0 text-color-dark !pt-[8px]',
                                                menu: () => '!left-[0]',
                                                input: () => '!m-0 !p-0',
                                            }}
                                            classNamePrefix="react_select_custom"
                                            noOptionsMessage={() => 'Нет опций'}
                                            isLoading={false}
                                            value={bonusSelectOptions.find(option => option.value === b.id)}
                                            onChange={(e) => setSelectedBonuses(prev => prev.map((item, index) => index === idx ? ({ ...item, id: e.value }) : item))}
                                            options={bonusSelectOptions}
                                            placeholder={"Выберите бонус"}
                                            required={true}
                                            menuPosition="fixed"
                                            menuPortalTarget={document.body}
                                            components={{
                                                DropdownIndicator: () => <ArrowDown height={20} width={20} />,
                                                IndicatorSeparator: () => null,
                                                ValueContainer: (props) => {
                                                    return (
                                                        <>
                                                            {props.hasValue &&
                                                                <div className="w-8 h-8 flex justify-center mr-2 items-center rounded-full overflow-hidden">
                                                                    <img src={bonuses?.find((bonus) => bonus.id === props.getValue()?.[0]?.value)?.image} className="w-full object-contain h-full" />
                                                                </div>
                                                            }
                                                            {props.children}
                                                        </>
                                                    )
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-span-4 items-end gap-2 flex">
                                    <div className={`flex w-full flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors['bonuses']?.[idx]?.amount && 'border-color-danger'}`}>
                                        <div className="flex justify-between w-full items-center">
                                            <label className="font-semibold uppercase text-base text-[#828282]">
                                                Количество
                                            </label>
                                            {errors['bonuses']?.[idx]?.amount &&
                                                <label className="text-base flex items-center text-color-danger">
                                                    <WarningSvg width={20} height={20} className="me-1" />
                                                    {errors['bonuses']?.[idx]?.amount.message}
                                                </label>
                                            }
                                        </div>
                                        <input
                                            placeholder={'Введите количество'}
                                            type={'number'}
                                            className={`w-full outline-0 mt-[4px] text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base outline-none text-color-dark`}
                                            value={b.amount}
                                            onChange={(e) => setSelectedBonuses(prev => prev.map((item, index) => index === idx ? ({ ...item, amount: Number(e.target.value) }) : item))}
                                        />
                                    </div>
                                    <div className="w-10 h-10 border rounded-xl flex items-center justify-center">
                                        <button onClick={() => removeBonus(idx)}>
                                            <XSvg width={16} height={16} />
                                        </button>
                                    </div>
                                </div>
                            </Fragment>
                        )}
                    </div>
                    <button onClick={addBonus} className='bg-[#f0efef] font-semibold relative mt-4 rounded-[4px] w-12 h-9'>
                        +
                    </button>
                </div>
                {/* <div className="box">
                    <div className="box_header flex justify-between items-center border-b !mb-4 !pb-4">
                        <div className="font-mediumttf">
                            Сообщение
                        </div>
                        {errors['template'] &&
                            <label className="text-base flex items-center text-color-danger">
                                <WarningSvg width={20} height={20} className="me-1" />
                                {errors['template'].message}
                            </label>
                        }
                    </div>
                    <div className="flex w-full">
                        <div className="flex-1 w-full">
                            <TelegramTextEditor
                                onInputChange={(e) => setValue('template', e as string)}
                            />
                        </div>
                    </div>
                </div> */}
                <div className="w-fit">
                    <button className="text-base px-12 h-[48px] w-full text-center bg-color-dark transition border-color-dark text-white font-medium border rounded-full hover:cursor-pointer hover:bg-color-dark hover:text-white" onClick={() => { handleSubmit(onSubmit)() }}>
                        Создать
                    </button>
                </div>
            </div>
        </div>
    )
}